/* eslint-disable @typescript-eslint/no-explicit-any */
import { apiConsumer } from ".";
import {
  BillImportOrder,
  BillImportResponse,
  SendBillToSessionBody,
  SendBillToSessionResult,
  CancelBillProductRestock,
  GetBillDetailResponse,
  GetTableBillsQueries,
  GetTableBillsResponse,
  PartialCancelBillBody,
  UpdateBillProduct,
} from "../interfaces/TableBills";
import { ChangeProductBody, ProductChangeCandidate } from "../interfaces/sales";
import { getBodyWithQueries, getUrlWithqueries } from "../utils/buildUrlWithQueries";

export const getAllTableBills = async (
  queries: GetTableBillsQueries,
): Promise<GetTableBillsResponse> => {
  const urlWithQueries = getUrlWithqueries("/bills", queries);

  const { data } = await apiConsumer({
    endpoint: urlWithQueries,
    method: "GET",
  });

  return {
    ...data,
    data: data.data.map((item: any) => {
      return {
        created_at: item.created_at,
        uuid: item.uuid,
        event: item.event.name,
        name: item.name,
        concession: getConcessionsString(item),
        products_quantity: item.total_products,
        seller: item.session.session_name || "Sin nombre de sessión",
        subtotal: item.subtotal,
        status: item.status,
        status_description: item.status_description,
      };
    }),
  };
};

export const getAllTableBillsPost = async (
  queries: GetTableBillsQueries,
): Promise<GetTableBillsResponse> => {
  const body = getBodyWithQueries(queries);

  const { data } = await apiConsumer({
    endpoint: "/bills/list",
    method: "POST",
    body,
  });

  return {
    ...data,
    data: data.data.map((item: any) => {
      return {
        created_at: item.created_at,
        uuid: item.uuid,
        event: item.event.name,
        name: item.name,
        concession: getConcessionsString(item),
        products_quantity: item.total_products,
        seller: item.session.session_name || "Sin nombre de sessión",
        subtotal: item.subtotal,
        status: item.status,
        status_description: item.status_description,
      };
    }),
  };
};

export const getBillDetail = async (
  uuid: string,
  concession?: string,
): Promise<GetBillDetailResponse> => {
  const endpoint = concession
    ? `/bills/${uuid}?concession=${concession}`
    : `/bills/${uuid}`;

  const { data } = await apiConsumer({
    endpoint,
    method: "GET",
  });

  return {
    ...data,
    sales: data.sales.map((sale: any) => {
      return {
        ...sale,
        products: sale.products.map((product: any) => {
          const isDiscount = product.price !== product.base_price;
          return {
            ...product,
            price_type:
              product.discount?.name ||
              product.promotion?.name ||
              product.external_discount_name ||
              product.combo_name ||
              (isDiscount ? "Descuento" : "Precio regular"),
          };
        }),
      };
    }),
  };
};

const EXCLUDED_SALE_STATUSES = [0, 4]; // Cancelada, Reembolsada

export const calculateTotalSubtotal = (bill: any): number => {
  return bill.sales.reduce((total: number, sale: any) => {
    if (EXCLUDED_SALE_STATUSES.includes(sale.status)) return total;
    return total + sale.subtotal;
  }, 0);
};

export const calculateTotalTip = (bill: any): number => {
  return bill.sales.reduce((total: number, sale: any) => {
    if (EXCLUDED_SALE_STATUSES.includes(sale.status)) return total;
    return total + sale.tip;
  }, 0);
};

export const calculateTotalProducts = (bill: any): number => {
  return bill.sales.reduce((total: number, sale: any) => {
    return (
      total +
      sale.products.reduce((productTotal: number, product: any) => {
        return productTotal + product.quantity;
      }, 0)
    );
  }, 0);
};

/**
 * Closes an open bill, marking all its sales as paid and confirming pending cash payments.
 * @param uuid - The bill UUID to close
 */
export const closeBillService = async (uuid: string): Promise<void> => {
  await apiConsumer({
    endpoint: `/bills/${uuid}/close`,
    method: "POST",
  });
};

export const cancelBillService = async (
  uuid: string,
  phoneNumber?: string,
  comment?: string,
  products?: CancelBillProductRestock[],
): Promise<void> => {
  await apiConsumer({
    endpoint: `/bills/${uuid}/cancel`,
    method: "POST",
    body: {
      comment: comment || '',
      ...(phoneNumber ? { phone_number: phoneNumber } : {}),
      ...(products && products.length > 0 ? { products } : {}),
    },
  });
};

export const partialCancelBillService = async (
  uuid: string,
  body: PartialCancelBillBody,
): Promise<any> => {
  const { data } = await apiConsumer({
    endpoint: `/bills/${uuid}/partial-cancel`,
    method: "POST",
    body: body,
  });
  return data;
};

/**
 * Updates the products of a bill, setting each one to its new quantity (0 removes it).
 * @param uuid - The bill UUID
 * @param products - The sale products with their resulting quantity
 */
export const updateBillProductsService = async (
  uuid: string,
  products: UpdateBillProduct[],
): Promise<void> => {
  await apiConsumer({
    endpoint: `/bills/${uuid}`,
    method: "PATCH",
    body: products,
  });
};

/**
 * YOSHI-10261: productos de la cuenta que se pueden cambiar, con sus opciones de reemplazo válidas
 * (misma concesión y mismo precio exacto).
 */
export const getBillProductChangeOptionsService = async (
  uuid: string,
): Promise<ProductChangeCandidate[]> => {
  const { data } = await apiConsumer({
    method: "GET",
    endpoint: `/bills/${uuid}/change-product/options`,
  });

  // YOSHI-10261: the endpoint answers the options array itself. Any other shape degrades to empty
  // instead of breaking the modal, which took the whole app down through the ErrorBoundary
  return Array.isArray(data) ? data : [];
};

/**
 * YOSHI-10261: cambia uno o varios productos de la cuenta por otro producto de la misma concesión y
 * del mismo precio exacto. No se recobra ni se reembolsa nada.
 */
export const changeBillProductsService = async (
  uuid: string,
  body: ChangeProductBody,
): Promise<any> => {
  const { data } = await apiConsumer({
    method: "POST",
    endpoint: `/bills/${uuid}/change-product`,
    body,
  });

  return data;
};

export const getConcessionsString = (bill: any): string => {
  const concessions = new Set<string>();

  bill.concessions.forEach((concession: any) => {
    concessions.add(concession.name);
  });

  const uniqueConcessions = Array.from(concessions);

  if (uniqueConcessions.length === 0) {
    return "-";
  }

  if (uniqueConcessions.length === 1) {
    return uniqueConcessions[0];
  }

  return `${uniqueConcessions.length} concesiones`;
};

/**
 * YOSHI-11055: imports orders of the Excel as pending external orders, each one in a bill of the
 * terminal session its rows name. The API answers with the count it imported and the rejected orders.
 */
export const importBillsService = async (
  orders: BillImportOrder[],
  uuidVenue: string,
  requestId?: string,
): Promise<BillImportResponse> => {
  const { data } = await apiConsumer({
    endpoint: `/bills/import/${uuidVenue}`,
    method: "POST",
    body: orders,
    ...(requestId && {
      headers: { "X-Idempotency-Key": requestId },
    }),
  });

  return {
    imported: data?.imported ?? 0,
    rejected: Array.isArray(data?.rejected) ? data.rejected : [],
  };
};

/**
 * YOSHI-11055: sends a pending external order (by its sale) to a terminal session as a bill, so that
 * terminal can charge it.
 */
export const sendBillToSessionService = async (
  saleUuid: string,
  body: SendBillToSessionBody,
): Promise<{ message: string; data: SendBillToSessionResult }> => {
  const { data } = await apiConsumer({
    endpoint: `/bills/send-to-session/${saleUuid}`,
    method: "POST",
    body,
  });

  return { message: data?.message ?? "", data: data?.data };
};
